import React, { useState, useEffect } from "react";
import { Box, BottomNavigation, BottomNavigationAction, Paper, SwipeableDrawer, Typography, IconButton, ListItemButton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import StarOutline from '@mui/icons-material/StarOutline';
import MenuIcon from '@mui/icons-material/Menu';
import SpaceList from "./SpaceList";
import SidebarCollapsibleSection from "./SidebarCollapsibleSection";
import AddIcon from '@mui/icons-material/Add';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { Skeleton, Button } from "@mui/material";
import { Link } from "react-router-dom";
import CreateSpace from "../Space/CreateSpace";
import DragAndDropActionDialog from "./DragAndDropActionDialog";
import AddToSpace from "../Home/AddToSpace";

const BottomNavigationBar = ({
  token,
  mySpaces,
  sharedSpaces,
  subscribedSpaces,
  isLoadingMySpaces,
  isLoadingSharedSpaces,
  isLoadingSubscribedSpaces,
  expandedSpaces,
  handleSpaceToggle,
  setIsCreateSpaceOpen,
  setIsSubSpaceMode,
  getMySpaces,
  getSharedSpaces,
  getSubscribedSpaces,
  myCollectionsOpen,
  sharedCollectionsOpen,
  subscribedCollectionsOpen,
  handleMyCollectionsToggle,
  handleSharedCollectionsToggle,
  handleSubscribedCollectionsToggle,
  loadingSubspaces,
  showSpacePopup,
  currentSpace,
  parentSpaceData,
  draggingSpaceId,
  setDraggingSpaceId,
  draggingSpace,
  setDraggingSpace,
  dragInProcess,
  setDragInProcess,
  dropDestinationId,
  setDropDestinationId,
  dropDestinationSpace,
  setDropDestinationSpace,
  setShowActionDialog,
  handleCollaboratorsOpen,
  setParentSpaceId,
  myCollectionsSearchVisible,
  setMyCollectionsSearchVisible,
  sharedCollectionsSearchVisible,
  setSharedCollectionsSearchVisible,
  subscribedCollectionsSearchVisible,
  setSubscribedCollectionsSearchVisible,
  myCollectionsSearchResults,
  sharedCollectionsSearchResults,
  subscribedCollectionsSearchResults,
  handleMyCollectionsSearch,
  handleSharedCollectionsSearch,
  handleSubscribedCollectionsSearch,
  onPlusClick,
  onChatClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collectionsDrawerOpen, setCollectionsDrawerOpen] = useState(false);
  const [exploreDrawerOpen, setExploreDrawerOpen] = useState(false);
  const bottomNavVisible = useSelector((state) => state.bottomNavVisible ?? true);

  // Load subscribed spaces when collections drawer opens
  useEffect(() => {
    if (collectionsDrawerOpen && subscribedSpaces.length === 0 && token) {
      getSubscribedSpaces();
    }
  }, [collectionsDrawerOpen, subscribedSpaces.length, token, getSubscribedSpaces]);

  // Determine which tab is active based on current route
  const getActiveTab = () => {
    if (location.pathname.includes('/community')) return 0;
    if (location.pathname.includes('/my-reviews')) return 1;
    // No active tab for Plus (2) and Chat (3) buttons
    if (location.pathname.includes('/spaces/')) return 4; // Collections might be active when viewing a space
    return false; // No active tab by default
  };

  const handleNavigationChange = (event, newValue) => {
    switch (newValue) {
      case 0:
        setExploreDrawerOpen(true);
        break;
      case 1:
        navigate('/my-reviews');
        break;
      case 2:
        // Plus button - open rate dialog
        if (onPlusClick) {
          onPlusClick();
        }
        break;
      case 3:
        // Chat button - open AI chat
        if (onChatClick) {
          onChatClick();
        }
        break;
      case 4:
        setCollectionsDrawerOpen(true);
        break;
      default:
        break;
    }
  };

  const handleMyCollectionsSearchToggle = (e) => {
    e.stopPropagation();
    setMyCollectionsSearchVisible(!myCollectionsSearchVisible);
  };

  const handleSharedCollectionsSearchToggle = (e) => {
    e.stopPropagation();
    setSharedCollectionsSearchVisible(!sharedCollectionsSearchVisible);
  };

  const handleSubscribedCollectionsSearchToggle = (e) => {
    e.stopPropagation();
    setSubscribedCollectionsSearchVisible(!subscribedCollectionsSearchVisible);
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <Paper 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0,
          zIndex: 1100,
          borderTop: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          transform: bottomNavVisible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'transform',
          visibility: bottomNavVisible ? 'visible' : 'hidden',
          pointerEvents: bottomNavVisible ? 'auto' : 'none',
        }} 
        elevation={3}
      >
        <BottomNavigation
          value={getActiveTab()}
          onChange={handleNavigationChange}
          showLabels
          sx={{
            height: 65,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '6px 8px',
              '&.Mui-selected': {
                color: 'rgb(99,102,241)',
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.7rem',
                  fontWeight: 600,
                }
              }
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.65rem',
              fontWeight: 500,
            }
          }}
        >
          <BottomNavigationAction 
            label="Explore" 
            icon={<GroupOutlinedIcon />}
          />
          <BottomNavigationAction 
            label="Reviews" 
            icon={<StarOutline />}
          />
          <BottomNavigationAction 
            label="Add" 
            icon={
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 16px rgba(99,102,241,0.4)',
                  }
                }}
              >
                <AddIcon sx={{ 
                  fontSize: 24, 
                  width: 24,
                  height: 24,
                  color: 'white',
                  flexShrink: 0,
                  display: 'block'
                }} />
              </Box>
            }
            sx={{
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                fontWeight: 700,
                color: 'rgb(99,102,241)',
              }
            }}
          />
          <BottomNavigationAction 
            label="AI Chat" 
            icon={<SmartToyIcon />}
          />
          <BottomNavigationAction 
            label="My Collections" 
            icon={<MenuIcon />}
          />
        </BottomNavigation>
      </Paper>

      {/* Explore Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={exploreDrawerOpen}
        onClose={() => setExploreDrawerOpen(false)}
        onOpen={() => setExploreDrawerOpen(true)}
        disableSwipeToOpen={false}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            maxHeight: '40vh',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            maxHeight: '40vh',
            overflowY: 'auto',
            pb: 2,
          }}
        >
          {/* Drag handle indicator */}
          <Box
            sx={{
              width: '40px',
              height: '4px',
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '2px',
              margin: '12px auto 8px auto',
            }}
          />

          {/* Explore Header */}
          <Typography 
            variant="h6" 
            sx={{ 
              px: 3, 
              py: 2, 
              fontWeight: 600,
              color: 'rgba(30,41,59,0.9)'
            }}
          >
            Explore
          </Typography>

          {/* Reviews Option */}
          <ListItemButton
            onClick={() => {
              navigate('/community');
              setExploreDrawerOpen(false);
            }}
            sx={{
              mx: 2,
              mb: 1,
              borderRadius: '12px',
              '&:hover': {
                backgroundColor: 'rgba(99,102,241,0.08)',
              },
            }}
          >
            <StarOutline sx={{ fontSize: 24, color: "rgba(99,102,241,0.8)", mr: 2 }} />
            <Typography sx={{ fontWeight: 500, color: 'rgba(30,41,59,0.9)' }}>
              Reviews
            </Typography>
          </ListItemButton>

          {/* Collections Option */}
          <ListItemButton
            onClick={() => {
              navigate('/community-spaces');
              setExploreDrawerOpen(false);
            }}
            sx={{
              mx: 2,
              mb: 1,
              borderRadius: '12px',
              '&:hover': {
                backgroundColor: 'rgba(99,102,241,0.08)',
              },
            }}
          >
            <MenuIcon sx={{ fontSize: 24, color: "rgba(99,102,241,0.8)", mr: 2 }} />
            <Typography sx={{ fontWeight: 500, color: 'rgba(30,41,59,0.9)' }}>
              Collections
            </Typography>
          </ListItemButton>

          {/* Top Users Option */}
          <ListItemButton
            onClick={() => {
              navigate('/top-users');
              setExploreDrawerOpen(false);
            }}
            sx={{
              mx: 2,
              mb: 1,
              borderRadius: '12px',
              '&:hover': {
                backgroundColor: 'rgba(99,102,241,0.08)',
              },
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: 24, color: "rgba(99,102,241,0.8)", mr: 2 }} />
            <Typography sx={{ fontWeight: 500, color: 'rgba(30,41,59,0.9)' }}>
              Top Users
            </Typography>
          </ListItemButton>
        </Box>
      </SwipeableDrawer>

      {/* Collections Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={collectionsDrawerOpen}
        onClose={() => setCollectionsDrawerOpen(false)}
        onOpen={() => setCollectionsDrawerOpen(true)}
        disableSwipeToOpen={false}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            maxHeight: '85vh',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            maxHeight: '85vh',
            overflowY: 'auto',
            pb: 2,
          }}
        >
          {/* Drag handle indicator */}
          <Box
            sx={{
              width: '40px',
              height: '4px',
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '2px',
              margin: '12px auto 8px auto',
            }}
          />

          {/* Collections Header */}
          <Typography 
            variant="h6" 
            sx={{ 
              px: 3, 
              py: 2, 
              fontWeight: 600,
              color: 'rgba(30,41,59,0.9)'
            }}
          >
            Collections
          </Typography>

          {/* Current Space section - only shown when viewing a space */}
          {currentSpace && (
            <>
              <Box sx={{ 
                height: "1px", 
                background: "linear-gradient(90deg, transparent 0%, rgba(226,232,240,0.8) 20%, rgba(226,232,240,0.8) 80%, transparent 100%)",
                my: 2,
                mx: 2
              }} />
              <Box sx={{ px: 3, py: 1, overflow: 'hidden', width: '100%', boxSizing: 'border-box' }}>
                {parentSpaceData ? (
                  <SpaceList
                    spaces={[parentSpaceData]}
                    open={true}
                    location={location}
                    navigate={navigate}
                    isMobile={true}
                    setMobileOpen={() => setCollectionsDrawerOpen(false)}
                    expandedSpaces={expandedSpaces}
                    handleSpaceToggle={handleSpaceToggle}
                    draggingSpaceId={draggingSpaceId}
                    setDraggingSpaceId={setDraggingSpaceId}
                    draggingSpace={draggingSpace}
                    setDraggingSpace={setDraggingSpace}
                    dragInProcess={dragInProcess}
                    setDragInProcess={setDragInProcess}
                    dropDestinationId={dropDestinationId}
                    setDropDestinationId={setDropDestinationId}
                    dropDestinationSpace={dropDestinationSpace}
                    setDropDestinationSpace={setDropDestinationSpace}
                    setShowActionDialog={setShowActionDialog}
                    handleCollaboratorsOpen={handleCollaboratorsOpen}
                    setIsCreateSpaceOpen={setIsCreateSpaceOpen}
                    setIsSubSpaceMode={setIsSubSpaceMode}
                    setParentSpaceId={setParentSpaceId}
                    disableDrag={true}
                    hideSearch={true}
                    isCurrentSpace={false}
                    loadingSubspaces={loadingSubspaces}
                    allowRatedObjectDrop={true}
                    showSpacePopup={showSpacePopup}
                  />
                ) : (
                  <SpaceList
                    spaces={[currentSpace]}
                    open={true}
                    location={location}
                    navigate={navigate}
                    isMobile={true}
                    setMobileOpen={() => setCollectionsDrawerOpen(false)}
                    expandedSpaces={expandedSpaces}
                    handleSpaceToggle={handleSpaceToggle}
                    draggingSpaceId={draggingSpaceId}
                    setDraggingSpaceId={setDraggingSpaceId}
                    draggingSpace={draggingSpace}
                    setDraggingSpace={setDraggingSpace}
                    dragInProcess={dragInProcess}
                    setDragInProcess={setDragInProcess}
                    dropDestinationId={dropDestinationId}
                    setDropDestinationId={setDropDestinationId}
                    dropDestinationSpace={dropDestinationSpace}
                    setDropDestinationSpace={setDropDestinationSpace}
                    setShowActionDialog={setShowActionDialog}
                    handleCollaboratorsOpen={handleCollaboratorsOpen}
                    setIsCreateSpaceOpen={setIsCreateSpaceOpen}
                    setIsSubSpaceMode={setIsSubSpaceMode}
                    setParentSpaceId={setParentSpaceId}
                    disableDrag={true}
                    hideSearch={true}
                    isCurrentSpace={false}
                    loadingSubspaces={loadingSubspaces}
                    allowRatedObjectDrop={true}
                    showSpacePopup={showSpacePopup}
                  />
                )}
              </Box>
              <Box sx={{ 
                height: "1px", 
                background: "linear-gradient(90deg, transparent 0%, rgba(226,232,240,0.8) 20%, rgba(226,232,240,0.8) 80%, transparent 100%)",
                my: 2,
                mx: 2
              }} />
            </>
          )}

          {/* My Collections */}
          <SidebarCollapsibleSection
            title="My Collections"
            open={true}
            icon={<MenuIcon sx={{ fontSize: 20, color: "rgba(71,85,105,0.7)" }} />}
            isSectionOpen={myCollectionsOpen}
            setIsSectionOpen={handleMyCollectionsToggle}
            rightIcon={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCreateSpaceOpen(true);
                    setIsSubSpaceMode(false);
                  }}
                  size="small"
                  sx={{
                    background: "rgba(99,102,241,0.1)",
                    borderRadius: "8px",
                    "&:hover": {
                      background: "rgba(99,102,241,0.2)",
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 18, color: "rgba(99,102,241,0.8)" }} />
                </IconButton>
                {myCollectionsOpen && (
                  <IconButton
                    onClick={handleMyCollectionsSearchToggle}
                    size="small"
                    sx={{ 
                      background: myCollectionsSearchVisible ? "rgba(99,102,241,0.15)" : "rgba(71,85,105,0.1)",
                      borderRadius: "8px",
                      '&:hover': {
                        background: myCollectionsSearchVisible ? "rgba(99,102,241,0.2)" : "rgba(71,85,105,0.15)",
                      },
                    }}
                  >
                    <SearchIcon sx={{ 
                      fontSize: 18, 
                      color: myCollectionsSearchVisible ? "rgba(99,102,241,0.8)" : "rgba(71,85,105,0.7)" 
                    }} />
                  </IconButton>
                )}
              </Box>
            }
          >
            {token ? (
              isLoadingMySpaces ? (
                <Box sx={{ p: 2 }}>
                  {[...Array(3)].map((_, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
                        <Skeleton variant="text" width={150} height={24} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <SpaceList
                  spaces={myCollectionsSearchResults !== null ? myCollectionsSearchResults : mySpaces}
                  open={true}
                  location={location}
                  navigate={navigate}
                  isMobile={true}
                  setMobileOpen={() => setCollectionsDrawerOpen(false)}
                  expandedSpaces={expandedSpaces}
                  handleSpaceToggle={handleSpaceToggle}
                  draggingSpaceId={draggingSpaceId}
                  setDraggingSpaceId={setDraggingSpaceId}
                  draggingSpace={draggingSpace}
                  setDraggingSpace={setDraggingSpace}
                  dragInProcess={dragInProcess}
                  setDragInProcess={setDragInProcess}
                  dropDestinationId={dropDestinationId}
                  setDropDestinationId={setDropDestinationId}
                  dropDestinationSpace={dropDestinationSpace}
                  setDropDestinationSpace={setDropDestinationSpace}
                  setShowActionDialog={setShowActionDialog}
                  handleCollaboratorsOpen={handleCollaboratorsOpen}
                  setIsCreateSpaceOpen={setIsCreateSpaceOpen}
                  setIsSubSpaceMode={setIsSubSpaceMode}
                  setParentSpaceId={setParentSpaceId}
                  showSearch={myCollectionsSearchVisible}
                  onSpacesUpdate={handleMyCollectionsSearch}
                  loadingSubspaces={loadingSubspaces}
                  allowRatedObjectDrop={true}
                  showSpacePopup={showSpacePopup}
                />
              )
            ) : (
              <Box sx={{ 
                p: 3, 
                textAlign: "center", 
                background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)",
                border: "1px solid rgba(99,102,241,0.2)", 
                borderRadius: "16px", 
                mx: 2, 
                my: 2,
              }}>
                <Typography variant="body2" sx={{ mb: 2, color: "rgba(30,41,59,0.8)" }}>
                  Sign in to view My Collections.
                </Typography>
                <Button 
                  component={Link} 
                  to="/login" 
                  variant="contained" 
                  size="small"
                  sx={{
                    background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                    borderRadius: "10px",
                    px: 3,
                  }}
                  onClick={() => setCollectionsDrawerOpen(false)}
                >
                  Sign in
                </Button>
              </Box>
            )}
          </SidebarCollapsibleSection>

          {/* Shared Collections */}
          <SidebarCollapsibleSection
            title="Shared Collections"
            open={true}
            icon={<ShareOutlinedIcon sx={{ fontSize: 20, color: "rgba(71,85,105,0.7)" }} />}
            isSectionOpen={sharedCollectionsOpen}
            setIsSectionOpen={handleSharedCollectionsToggle}
            rightIcon={
              sharedCollectionsOpen && (
                <IconButton
                  onClick={handleSharedCollectionsSearchToggle}
                  size="small"
                  sx={{
                    background: sharedCollectionsSearchVisible ? "rgba(99,102,241,0.15)" : "rgba(71,85,105,0.1)",
                    borderRadius: "8px",
                    '&:hover': {
                      background: sharedCollectionsSearchVisible ? "rgba(99,102,241,0.2)" : "rgba(71,85,105,0.15)",
                    },
                  }}
                >
                  <SearchIcon sx={{ 
                    fontSize: 18, 
                    color: sharedCollectionsSearchVisible ? "rgba(99,102,241,0.8)" : "rgba(71,85,105,0.7)" 
                  }} />
                </IconButton>
              )
            }
          >
            {token ? (
              isLoadingSharedSpaces ? (
                <Box sx={{ p: 2 }}>
                  {[...Array(3)].map((_, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
                        <Skeleton variant="text" width={150} height={24} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <SpaceList
                  disableDrag={true}
                  spaces={sharedCollectionsSearchResults !== null ? sharedCollectionsSearchResults : sharedSpaces}
                  open={true}
                  location={location}
                  navigate={navigate}
                  isMobile={true}
                  setMobileOpen={() => setCollectionsDrawerOpen(false)}
                  expandedSpaces={expandedSpaces}
                  handleSpaceToggle={handleSpaceToggle}
                  draggingSpaceId={draggingSpaceId}
                  setDraggingSpaceId={null}
                  draggingSpace={draggingSpace}
                  setDraggingSpace={setDraggingSpace}
                  dragInProcess={dragInProcess}
                  setDragInProcess={setDragInProcess}
                  dropDestinationId={dropDestinationId}
                  setDropDestinationId={setDropDestinationId}
                  dropDestinationSpace={dropDestinationSpace}
                  setDropDestinationSpace={setDropDestinationSpace}
                  setShowActionDialog={setShowActionDialog}
                  handleCollaboratorsOpen={handleCollaboratorsOpen}
                  setIsCreateSpaceOpen={setIsCreateSpaceOpen}
                  setIsSubSpaceMode={setIsSubSpaceMode}
                  setParentSpaceId={setParentSpaceId}
                  showSearch={sharedCollectionsSearchVisible}
                  onSpacesUpdate={handleSharedCollectionsSearch}
                  loadingSubspaces={loadingSubspaces}
                  allowRatedObjectDrop={true}
                  showSpacePopup={showSpacePopup}
                />
              )
            ) : (
              <Box sx={{ 
                p: 3, 
                textAlign: "center", 
                background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)",
                border: "1px solid rgba(99,102,241,0.2)", 
                borderRadius: "16px", 
                mx: 2, 
                my: 2,
              }}>
                <Typography variant="body2" sx={{ mb: 2, color: "rgba(30,41,59,0.8)" }}>
                  Sign in to view Shared Collections.
                </Typography>
                <Button 
                  component={Link} 
                  to="/login" 
                  variant="contained" 
                  size="small"
                  sx={{
                    background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                    borderRadius: "10px",
                    px: 3,
                  }}
                  onClick={() => setCollectionsDrawerOpen(false)}
                >
                  Sign in
                </Button>
              </Box>
            )}
          </SidebarCollapsibleSection>

          {/* Subscribed Collections */}
          <SidebarCollapsibleSection
            title="Subscribed"
            open={true}
            icon={<NotificationsActiveIcon sx={{ fontSize: 20, color: "rgba(71,85,105,0.7)" }} />}
            isSectionOpen={subscribedCollectionsOpen}
            setIsSectionOpen={(newOpen) => {
              handleSubscribedCollectionsToggle(newOpen);
              if (newOpen && subscribedSpaces.length === 0) {
                getSubscribedSpaces();
              }
            }}
            rightIcon={
              subscribedCollectionsOpen && (
                <IconButton
                  onClick={handleSubscribedCollectionsSearchToggle}
                  size="small"
                  sx={{
                    background: subscribedCollectionsSearchVisible ? "rgba(99,102,241,0.15)" : "rgba(71,85,105,0.1)",
                    borderRadius: "8px",
                    '&:hover': {
                      background: subscribedCollectionsSearchVisible ? "rgba(99,102,241,0.2)" : "rgba(71,85,105,0.15)",
                    },
                  }}
                >
                  <SearchIcon sx={{ 
                    fontSize: 18, 
                    color: subscribedCollectionsSearchVisible ? "rgba(99,102,241,0.8)" : "rgba(71,85,105,0.7)" 
                  }} />
                </IconButton>
              )
            }
          >
            {token ? (
              isLoadingSubscribedSpaces ? (
                <Box sx={{ p: 2 }}>
                  {[...Array(3)].map((_, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
                        <Skeleton variant="text" width={150} height={24} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (subscribedCollectionsSearchResults !== null ? subscribedCollectionsSearchResults : subscribedSpaces).length > 0 ? (
                <SpaceList
                  disableDrag={true}
                  spaces={subscribedCollectionsSearchResults !== null ? subscribedCollectionsSearchResults : subscribedSpaces}
                  open={true}
                  location={location}
                  navigate={navigate}
                  isMobile={true}
                  setMobileOpen={() => setCollectionsDrawerOpen(false)}
                  expandedSpaces={expandedSpaces}
                  handleSpaceToggle={handleSpaceToggle}
                  draggingSpaceId={draggingSpaceId}
                  setDraggingSpaceId={null}
                  draggingSpace={draggingSpace}
                  setDraggingSpace={setDraggingSpace}
                  dragInProcess={dragInProcess}
                  setDragInProcess={setDragInProcess}
                  dropDestinationId={dropDestinationId}
                  setDropDestinationId={setDropDestinationId}
                  dropDestinationSpace={dropDestinationSpace}
                  setDropDestinationSpace={setDropDestinationSpace}
                  setShowActionDialog={setShowActionDialog}
                  handleCollaboratorsOpen={handleCollaboratorsOpen}
                  setIsCreateSpaceOpen={setIsCreateSpaceOpen}
                  setIsSubSpaceMode={setIsSubSpaceMode}
                  setParentSpaceId={setParentSpaceId}
                  showSearch={subscribedCollectionsSearchVisible}
                  onSpacesUpdate={handleSubscribedCollectionsSearch}
                  loadingSubspaces={loadingSubspaces}
                  allowRatedObjectDrop={true}
                  showSpacePopup={showSpacePopup}
                />
              ) : (
                <Box sx={{ 
                  p: 3, 
                  textAlign: "center",
                  background: "rgba(71,85,105,0.04)",
                  borderRadius: "12px",
                  mx: 2,
                  my: 1
                }}>
                  <Typography variant="body2" sx={{ color: "rgba(71,85,105,0.7)" }}>
                    No subscribed collections yet
                  </Typography>
                </Box>
              )
            ) : (
              <Box sx={{ 
                p: 3, 
                textAlign: "center", 
                background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)",
                border: "1px solid rgba(99,102,241,0.2)", 
                borderRadius: "16px", 
                mx: 2, 
                my: 2,
              }}>
                <Typography variant="body2" sx={{ mb: 2, color: "rgba(30,41,59,0.8)" }}>
                  Sign in to view Subscribed Collections.
                </Typography>
                <Button 
                  component={Link} 
                  to="/login" 
                  variant="contained" 
                  size="small"
                  sx={{
                    background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                    borderRadius: "10px",
                    px: 3,
                  }}
                  onClick={() => setCollectionsDrawerOpen(false)}
                >
                  Sign in
                </Button>
              </Box>
            )}
          </SidebarCollapsibleSection>
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default BottomNavigationBar;

